import { useRowClick } from '@/lib/navigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { processHowOftenResultsState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import type { TaskType } from '@rock/types';
import type { FC } from 'react';

export const ProcessHowOftenResultsTable: FC<{ taskType: TaskType }> = ({ taskType }) => {
    const typeId = taskType.id;
    const [results, resultsLoadable] = useLoadableAtomValue(processHowOftenResultsState(typeId), []);
    const onRowClick = useRowClick();

    if (resultsLoadable.state === 'loading') {
        return (
            <Card>
                <CardContent className="p-6">
                    <div className="text-muted-foreground">Loading process how often results…</div>
                </CardContent>
            </Card>
        );
    }

    if (resultsLoadable.state === 'hasError') {
        return (
            <Card>
                <CardContent className="p-6">
                    <div className="text-destructive">
                        Error loading process how often results:{' '}
                        {resultsLoadable.error instanceof Error
                            ? resultsLoadable.error.message
                            : String(resultsLoadable.error)}
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader className="border-b-1 pb-3">
                <CardTitle className="text-xl">Process How Often Results</CardTitle>
            </CardHeader>
            <CardContent>
                {results.length === 0 ? (
                    <div className="text-muted-foreground">No process how often results found for this task type.</div>
                ) : (
                    <div className="px-4 pb-4">
                        <Table className="not-prose">
                            <TableHeader className="bg-secondary">
                                <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Subscription Changed</TableHead>
                                    <TableHead>Summary</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {results.map((result) => (
                                    <TableRow
                                        key={result.id}
                                        onClick={onRowClick(`./${result.id}`)}
                                        className="cursor-pointer"
                                    >
                                        <TableCell>{new Date(result.createdAt).toLocaleString()}</TableCell>
                                        <TableCell>
                                            <span
                                                className={
                                                    result.subscriptionChanged ? 'text-green-600' : 'text-gray-600'
                                                }
                                            >
                                                {result.subscriptionChanged ? 'Yes' : 'No'}
                                            </span>
                                        </TableCell>
                                        <TableCell className="max-w-md truncate">{result.result.summary}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
